import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navmenu from './Navmenu';
import MobileMenu from './components/MobileMenu';
import OptionMenu from './components/MenuBar';
import { NotificationBell, NotificationDropdown } from './components/Notifications';
import LanguageSwitcher from './components/LanguageSwitcher';
import { AuthContext } from './components/Provider';
import { FaCode, FaSlidersH } from 'react-icons/fa';
import './HeaderL.css';

// Developer user ID constant
const DEVELOPER_USER_ID = '3f3d24fd-cf1f-42c8-b12e-3cfa5948c17d';

function Header() {
  // Access the authenticated user and loading state from AuthContext
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const [shouldWrap, setShouldWrap] = useState(false);
  
  // Only show OptionButton (theme) and OptionMenu (profile) when user is logged in
  const isAuthenticated = !!user;
  const isDeveloper = user && user.id === DEVELOPER_USER_ID;
  
  // Function to check if header should wrap
  const checkHeaderWrap = () => {
    if (!headerRef.current) return;
    
    const header = headerRef.current;
    const logo = header.querySelector('.logo');
    const nav = header.querySelector('.nav-section');
    const controls = header.querySelector('.controls-section');
    
    if (!logo || !nav || !controls) return;
    
    // Force single line temporarily to get accurate measurements
    const originalClass = header.className;
    header.className = 'header-top single-line';
    
    // Force a reflow to ensure measurements are accurate
    void header.offsetHeight;
    
    // Get the actual widths
    const logoWidth = logo.getBoundingClientRect().width;
    const navWidth = nav.getBoundingClientRect().width;
    const controlsWidth = controls.getBoundingClientRect().width;
    const containerWidth = header.getBoundingClientRect().width;
    const gaps = 40; // Account for gaps and padding
    
    const totalContentWidth = logoWidth + navWidth + controlsWidth + gaps;
    
    // Restore original class
    header.className = originalClass;
    
    // Only wrap if content actually doesn't fit
    const needsWrap = totalContentWidth > containerWidth;
    setShouldWrap(needsWrap);
  };
  
  // Check on mount and resize
  useEffect(() => {
    checkHeaderWrap();
    
    const handleResize = () => {
      checkHeaderWrap();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Also check when navigation items might change
    const observer = new ResizeObserver(() => {
      checkHeaderWrap();
    });
    
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [isAuthenticated]); // Re-check when auth state changes

  return (
    <div className="header-container">
      <header 
        ref={headerRef}
        className={`header-top ${shouldWrap ? 'wrapped' : 'single-line'}`}
      >
        <div className="logo">
          <p className="logoName" onClick={() => navigate('/home')}>
            <span>Brauksim</span>
            <span className="colored-part">Kopā</span>
          </p>
        </div>
        
        <div className='nav-section'>
          <Navmenu/>
        </div>
        
        <div className='controls-section'>
          <div className="user-controls">
            {/* Developer button - only show for specific user */}
            {isDeveloper && (
              <button 
                className="developer-button"
                onClick={() => navigate('/developer')}
                title="Developer Dashboard"
              >
                <FaCode className="developer-icon" />
              </button>
            )}
            
            {/* Show notification bell when logged in */}
            {isAuthenticated && (
              <div className="NTS-notification-bell-container">
                <NotificationBell />
                <NotificationDropdown />
              </div>
            )}
            
            {/* Settings button - now in header */}
            {isAuthenticated && (
              <button 
                className="settings-button"
                onClick={() => navigate('/settings')}
                title="Settings"
              >
                <FaSlidersH className="settings-icon" />
              </button>
            )}
            
            {/* Only show user menu when logged in */}
            {isAuthenticated && <OptionMenu />}
            
            {/* Show language switcher for non-logged-in users */}
            {!isAuthenticated && !loading && <LanguageSwitcher />}
            
            {/* When not authenticated, show login button */}
            {!isAuthenticated && !loading && (
              <a href="/login" className="login-button">
                Login
              </a>
            )}
          </div>
          
          {/* Mobile Menu - always rightmost element, only visible on mobile */}
          <MobileMenu />
        </div>
      </header>
    </div>
  );
}
  
export default Header;